import { useState } from 'react'
import { X, Mail, AlertCircle } from 'lucide-react'

interface VerificationBannerProps {
  onResend: () => Promise<void>
}

export function VerificationBanner({ onResend }: VerificationBannerProps) {
  const [isDismissed, setIsDismissed] = useState(() => {
    return localStorage.getItem('verification-banner-dismissed') === 'true'
  })
  const [isResending, setIsResending] = useState(false)
  const [message, setMessage] = useState<string | null>(null)

  const handleDismiss = () => {
    localStorage.setItem('verification-banner-dismissed', 'true')
    setIsDismissed(true)
  }

  const handleResend = async () => {
    try {
      setIsResending(true)
      setMessage(null)
      await onResend()
      setMessage('Verification email sent! Please check your inbox.')
    } catch (error) {
      setMessage('Failed to send verification email. Please try again.')
    } finally {
      setIsResending(false)
    }
  }

  if (isDismissed) return null

  return (
    <div className="bg-yellow-50 border-b border-yellow-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center flex-1">
            <AlertCircle className="h-5 w-5 text-yellow-600 mr-3 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm text-yellow-800">
                Please verify your email to receive invoices, receipts, and important updates about your subscriptions.
              </p>
              {message && (
                <p className={`text-xs mt-1 ${message.includes('Failed') ? 'text-red-600' : 'text-green-600'}`}>
                  {message}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center ml-4 space-x-2">
            <button
              onClick={handleResend}
              disabled={isResending}
              className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-yellow-700 bg-yellow-100 hover:bg-yellow-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Mail className="h-3.5 w-3.5 mr-1.5" />
              {isResending ? 'Sending...' : 'Resend Email'}
            </button>
            <button
              onClick={handleDismiss}
              className="text-yellow-600 hover:text-yellow-800 focus:outline-none"
              aria-label="Dismiss"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
